<template>
    <div>
        <simple-button
            :disabled="disabled"
            display="secondary"
            icon="mail"
            look="primary"
            class="ml-auto"
            @click="startRequestingExtension"
        >{{ $t('applications.ask_retention_extension') }}</simple-button>

        <popup-modal
            v-if="requestingExtension"
            @close="requestingExtension = false"
            :close-on-click="!form.isDirty()"
            :title="$t('applications.ask_retention_extension')"
        >
            <div class="w-768">

                <simple-input
                    v-model="form.period"
                    type="number"
                    class="mb-16"
                    required
                >
                    {{ $t('applications.retention_extension_period') }}
                    <template #tip>{{ $t('applications.retention_extension_period_description') }}</template>
                </simple-input>

                <simple-input
                    v-model="form.subject"
                    class="mb-16"
                    required
                >
                    {{ $t('users.email') }}  {{ $t('mailer.subject').toLocaleLowerCase() }}
                </simple-input>
                <div
                    v-if="errors.subject !== undefined && errors.subject.length"
                    class="flex items-center text-semantic-error text-body-sm mb-16"
                >
                    <svg class="block fill-current flex-none trans-ease max-w-12 max-h-12 mr-4">
                        <use xlink:href="#warning"></use>
                    </svg>
                    <span>{{ errors.subject }}</span>
                </div>

                <tabs
                    :active-key="activeLocale"
                    class="inline-flex w-full mb-16"
                    :items="languages"
                    scroll
                    @set-active="switchLanguage"
                />

                <wysiwyg
                    ref="content"
                    v-model="form.message"
                    :use-min-height="false"
                >
                    {{ $t('applications.optional_message') }}
                    <template #tip>{{ $t('applications.optional_message_description') }}</template>
                </wysiwyg>

                <div class="flex items-center justify-end pt-32 space-x-24">
                    <simple-button
                        @click="previewMail"
                        look="secondary"
                        icon="mail"
                    >
                        {{ $t('general.preview_mail') }}
                    </simple-button>

                    <simple-button
                        display="secondary"
                        icon="send"
                        :loading="startedExtensionRequest"
                        look="primary"
                        @click="validateBeforeSubmit"
                    >
                        {{ $t('general.submit') }}
                    </simple-button>
                </div>
            </div>
        </popup-modal>

        <popup-modal-confirm
            v-if="!!bulkApplicationData"
            confirmButton="success"
            :confirmText="$t('general.confirm')"
            :message="$tc('applications.extension_confirmation_message', bulkCount, {count: bulkCount, period: form.period})"
            :working="sendingExtensionRequest"
            @close="cancelVerification"
            @confirm="sendExtensionRequest"
        ></popup-modal-confirm>


        <preview-mail v-if="startPreviewMail"
                      @close="endPreviewMail"
                      :content="previewMailContent"
        ></preview-mail>
    </div>
</template>

<script>
import Form from '../../etc/forms/Form';
import InputVue from '../forms/Input.vue';
import Wysiwyg from '../forms/Wysiwyg.vue';
import PopupModalVue from './PopupModal.vue';
import SimpleButton from './SimpleButton.vue';
import * as extensionRequests from '../../services/ExtensionRequestService';
import * as templates from '../../services/EmailTemplateService';
import * as bulkApplicationKey from '../../services/BulkApplicationKeyService';
import PopupModalConfirm from './PopupModalConfirm.vue';
import Application from '../../models/Application';
import {ADD_NOTIFICATION} from '../../state/mutation-types';
import {mapActions, mapState} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useNotificationsStore} from '../../state/modules/notifications';
import EmailTemplate from '../../models/EmailTemplate';
import {normalizeJson} from '../../../shared/helpers';
import PreviewMail from '../../PreviewMail';
import SvgIcon from './SvgIcon';
import Tabs from "../navigation/Tabs.vue";

export default {
    components: {
        Tabs,
        SvgIcon,
        PreviewMail,
        'simple-button': SimpleButton,
        'popup-modal': PopupModalVue,
        'simple-input': InputVue,
        'wysiwyg': Wysiwyg,
        'popup-modal-confirm': PopupModalConfirm,
    },
    props: {
        disabled: Boolean,
        requestFilters: {
            type: Object,
            default: () => ({}),
        },
        selection: {
            type: Object,
            default: () => ({
                applications: [],
                stages: []
            }),
        },
        totalApplications: {
            type: Number,
            default: 0,
        },
        type: {
            type: String,
            default: 'selection',
            validator: value => ['filter', 'selection', 'both'].includes(value),
        },
    },
    setup() {
        const mainStore = useMainStore();
        return {mainStore};
    },
    data: function () {
        return {
            errors: [],
            form: this.generateForm(),
            requestingExtension: false,
            startedExtensionRequest: false,
            sendingExtensionRequest: false,
            bulkApplicationData: null,
            startPreviewMail: false,
            previewMailContent: null,
            activeLocale: null,
        }
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany', 'locale']),
        active_locale() {
            return this.currentCompany.jobsite.active_languages.includes(this.locale) ? this.locale : this.currentCompany.jobsite.active_languages[0];
        },
        selectedStageIds () {
            return this.selection['stages'] || [];
        },
        getFilters () {
            return ['filter', 'both'].includes(this.type) ? this.requestFilters : {};
        },
        selectedApplications () {
            return ['selection', 'both'].includes(this.type) ? this.selection['applications'] || [] : [];
        },
        selectedApplicationIds () {
            return this.selectedApplications.map(app => app.id);
        },
        bulkCount() {
            if (!this.bulkApplicationData)
                return 0;

            return this.bulkApplicationData.attributes.applications_count
        },
        template() {
            return useRepo(EmailTemplate)
                .with('attachments')
                .where('type', 'retention_extension')
                .first();
        },
        languages() {
            if (this.currentCompany.jobsite.active_languages.length === 1) {
                return;
            }

            let languages = {};

            this.currentCompany.jobsite.active_languages.forEach((field, index) => {
                languages[field] = {
                    title: field,
                };
            });

            return languages;
        },
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        generateForm() {
            let fields = [
                { name: 'period', value: this.mainStore.currentCompany.data_retention_period },
                { name: 'subject', value: '' },
                { name: 'message', value: '<p></p>' },
            ];

            return new Form(fields);
        },
        validateBeforeSubmit () {

            this.startedExtensionRequest = true;

            this.errors = [];

            if (!this.form.data.subject.length) {
                this.errors = {...this.errors, subject: this.$t('validation.contact_subject_required')};

                this.startedExtensionRequest = false;
                return;
            }

            this.getBulkApplicationKey();
        },
        getBulkApplicationKey () {
            return this.form.submit(data => bulkApplicationKey.get({data: {
                selection: {
                    applications: this.selectedApplicationIds,
                    stages: this.selectedStageIds,
                }
            }}, this.getFilters)).then(data => {
                this.bulkApplicationData = data;
            });
        },
        startRequestingExtension() {
            this.requestingExtension = true;
        },
        stopRequestingExtension() {
            this.requestingExtension = false;
        },
        sendExtensionRequest() {
            this.sendingExtensionRequest = true;

            return this.form.submit(data => extensionRequests.storeBulk({data: {
                bulkApplicationId: this.bulkApplicationData.id,
                attributes: {
                    period: data.period,
                    subject: data.subject,
                    message: data.message,
                }
            }})).then(result => {

                if (this.type === 'filter') {
                    useRepo(Application).flush();
                } else {
                    this.selection['applications'].forEach(application => {
                        useRepo(Application).destroy(application.id);
                    });
                }

                this.$emit('sent');

            }).finally(() => {

                this.cancelVerification();
                this.sendingExtensionRequest = false;
                this.requestingExtension = false;

                return this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('applications.extension_request_sent'),
                });
            });
        },
        cancelVerification() {
            this.bulkApplicationData = null;
            this.startedExtensionRequest = false;
        },
        previewMail(){
            this.startPreviewMail = true;

            extensionRequests.preview({data: {attributes: {
                applicationId: this.selection.applications[0].id,
                message: this.form.data.message
            }}}).then(result => {
                this.previewMailContent = result;
            });
        },
        endPreviewMail(){
            this.startPreviewMail = false;
            this.previewMailContent = null;
        },
        switchLanguage(locale) {
            this.activeLocale = locale;
            if (this.template) {
                this.form.subject = this.template.subject[locale];
                this.$refs.content.setContent(this.template.content[locale]);
            }
        },
    },
    mounted() {
        this.activeLocale = this.active_locale;

        templates.index('retention_extension').then(result => {
            return useRepo(EmailTemplate).save(normalizeJson(result.data));
        }).then(inserts => {
            this.template = inserts[0];
            this.form.subject = this.template.subject[this.active_locale];
            this.form.message = this.template.content[this.active_locale];
        });
    }
}
</script>
